import { Stethoscope, Trash2, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { customerApi } from '../customer_api';

interface VetGrant {
  id: string;
  vetEmail: string;
  vetName: string | null;
  grantedAt: string;
  expiresAt: string | null;
}

/**
 * Share-with-my-vet panel embedded on the customer's pet detail page.
 *
 * - Lists active grants for this pet.
 * - Invite form: vetEmail + (optional) vetName → POST creates the grant +
 *   fires vet.invited email with magic-link to vet.kennel.app.
 * - Revoke per row.
 */
export function VetSharingPanel({ petId }: { petId: string }) {
  const [grants, setGrants] = useState<VetGrant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vetEmail, setVetEmail] = useState('');
  const [vetName, setVetName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);

  async function refresh() {
    try {
      const res = await customerApi<{ items: VetGrant[] }>(
        `/api/customer/pets/${encodeURIComponent(petId)}/vet-grants`,
      );
      setGrants(res.items);
      setError(null);
    } catch (err) {
      const e = err as Error;
      setError(e.message ?? 'Could not load vet grants.');
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId]);

  async function handleInvite(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!vetEmail.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await customerApi(`/api/customer/pets/${encodeURIComponent(petId)}/vet-grants`, {
        method: 'POST',
        body: JSON.stringify({
          vetEmail: vetEmail.trim(),
          vetName: vetName.trim() || undefined,
        }),
      });
      setVetEmail('');
      setVetName('');
      setConfirmMessage('Invitation sent. Your vet will receive a sign-in link by email.');
      await refresh();
    } catch (err) {
      const e = err as { body?: { issues?: string[] }; message?: string };
      setError(e.body?.issues?.[0] ?? e.message ?? 'Could not send the invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(grantId: string) {
    if (!confirm('Revoke this vet’s access? They will lose access immediately.')) return;
    try {
      await customerApi(
        `/api/customer/pets/${encodeURIComponent(petId)}/vet-grants/${encodeURIComponent(grantId)}`,
        { method: 'DELETE' },
      );
      setConfirmMessage('Access revoked.');
      await refresh();
    } catch (err) {
      const e = err as Error;
      setError(e.message ?? 'Could not revoke access.');
    }
  }

  return (
    <section
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        border: '1px solid var(--border, oklch(0.88 0.005 75))',
        borderRadius: '0.75rem',
        background: 'var(--card, white)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.5rem',
        }}
      >
        <Stethoscope size={16} />
        <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>Share with my vet</h3>
      </div>
      <p
        style={{
          fontSize: '0.85rem',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          marginTop: 0,
        }}
      >
        Give your vet read-only access to this pet's vaccinations, incident log, and behaviour notes
        — across every kennel you've stayed at that uses our platform.
      </p>

      <form
        onSubmit={handleInvite}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}
      >
        <input
          type="email"
          required
          value={vetEmail}
          onChange={(e) => setVetEmail(e.target.value)}
          placeholder="Vet email"
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--input, oklch(0.88 0.005 75))',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
          }}
        />
        <input
          type="text"
          value={vetName}
          onChange={(e) => setVetName(e.target.value)}
          placeholder="Vet name (optional)"
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--input, oklch(0.88 0.005 75))',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
          }}
        />
        <button
          type="submit"
          disabled={submitting}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            padding: '0.5rem 0.75rem',
            background: '#004AAD',
            color: 'white',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            border: 'none',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          <UserPlus size={14} />
          {submitting ? 'Sending…' : 'Send invitation'}
        </button>
      </form>

      {confirmMessage ? (
        <p
          style={{
            marginTop: '0.5rem',
            fontSize: '0.85rem',
            color: 'var(--success, oklch(0.55 0.12 155))',
          }}
        >
          {confirmMessage}
        </p>
      ) : null}
      {error ? (
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'oklch(0.55 0.18 25)' }}>
          {error}
        </p>
      ) : null}

      <div style={{ marginTop: '1rem' }}>
        {grants === null ? (
          <p
            style={{ fontSize: '0.85rem', color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
          >
            Loading…
          </p>
        ) : grants.length === 0 ? (
          <p
            style={{ fontSize: '0.85rem', color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
          >
            No vets have been granted access yet.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {grants.map((g) => (
              <li
                key={g.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0',
                  borderTop: '1px solid var(--border, oklch(0.88 0.005 75))',
                }}
              >
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 500 }}>
                    {g.vetName ?? g.vetEmail}
                  </p>
                  {g.vetName ? (
                    <p
                      style={{
                        margin: 0,
                        fontSize: '0.8rem',
                        color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                      }}
                    >
                      {g.vetEmail}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => handleRevoke(g.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.375rem 0.625rem',
                    background: 'transparent',
                    border: '1px solid var(--border, oklch(0.88 0.005 75))',
                    borderRadius: '0.375rem',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} /> Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
